import React, { useEffect, useMemo, useState } from "react";

type Standing = {
  name: string;
  points: number;
  codeCardsEarned: number;
  codeCardsGivenOut: number;
};

type WeeklyChallenge = {
  id: string;
  title: string;
  short?: string | null;
  weekStart: string;
  weekEnd: string;
  reward: string;
};

type PlayerChallenge = {
  playerName: string;
  challengeId: string;
};

const API_BASE =
  "https://sopronpogo-api.greenmeadow-9f8cab91.polandcentral.azurecontainerapps.io/api";

const WEEKLY_URL = `${API_BASE}/weekly-challenges/get-all`;
const STANDINGS_URL = `${API_BASE}/standings/get`;
const STANDINGS_GENERATE_URL = `${API_BASE}/standings/generate`;
const PLAYER_CHALLENGES_URL = `${API_BASE}/player-challenges/get`;
const REPLACE_URL =
  `${API_BASE}/player-challenges/replace-by-challenge-ids`;

function normalize(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function createPairKey(
  playerName: string,
  challengeId: string
): string {
  return `${normalize(playerName)}\u001F${normalize(challengeId)}`;
}

function getTodayDate(): string {
  const today = new Date();

  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isCurrentChallenge(
  challenge: WeeklyChallenge,
  today: string
): boolean {
  return (
    challenge.weekStart <= today &&
    challenge.weekEnd >= today
  );
}

export default function PlayerChallengeMatrix() {
  const [standings, setStandings] = useState<Standing[]>([]);
  const [weeklyChallenges, setWeeklyChallenges] = useState<
    WeeklyChallenge[]
  >([]);

  const [selectedChallengeIds, setSelectedChallengeIds] =
    useState<Set<string>>(new Set());

  const [completedPairs, setCompletedPairs] =
    useState<Set<string>>(new Set());

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] =
    useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    async function loadData() {
      setIsLoading(true);
      setError(null);

      try {
        const [
          weeklyResponse,
          standingsResponse,
          playerChallengesResponse,
        ] = await Promise.all([
          fetch(WEEKLY_URL),
          fetch(STANDINGS_URL),
          fetch(PLAYER_CHALLENGES_URL),
        ]);

        if (!weeklyResponse.ok) {
          throw new Error(
            "Nem sikerült betölteni a challenge-eket."
          );
        }

        if (!standingsResponse.ok) {
          throw new Error(
            "Nem sikerült betölteni a játékosokat."
          );
        }

        if (!playerChallengesResponse.ok) {
          throw new Error(
            "Nem sikerült betölteni a teljesítéseket."
          );
        }

        const weeklyData =
          (await weeklyResponse.json()) as WeeklyChallenge[];

        const standingsData =
          (await standingsResponse.json()) as Standing[];

        const playerChallengesData =
          (await playerChallengesResponse.json()) as PlayerChallenge[];

        if (!alive) {
          return;
        }

        const today = getTodayDate();

        const currentChallengeIds = weeklyData
          .filter((challenge) =>
            isCurrentChallenge(challenge, today)
          )
          .map((challenge) => challenge.id);

        const loadedPairs = new Set(
          playerChallengesData.map((item) =>
            createPairKey(
              item.playerName,
              item.challengeId
            )
          )
        );

        setWeeklyChallenges(weeklyData);
        setStandings(standingsData);
        setSelectedChallengeIds(
          new Set(currentChallengeIds)
        );
        setCompletedPairs(loadedPairs);
      } catch (loadError) {
        if (!alive) {
          return;
        }

        setError(
          loadError instanceof Error
            ? loadError.message
            : "Ismeretlen hiba történt."
        );
      } finally {
        if (alive) {
          setIsLoading(false);
        }
      }
    }

    void loadData();

    return () => {
      alive = false;
    };
  }, []);

  const selectedChallenges = useMemo(() => {
    return weeklyChallenges.filter((challenge) =>
      selectedChallengeIds.has(challenge.id)
    );
  }, [weeklyChallenges, selectedChallengeIds]);

  const selectedChallengeIdList = useMemo(() => {
    return selectedChallenges.map(
      (challenge) => challenge.id
    );
  }, [selectedChallenges]);

  const toggleVisibleChallenge = (
    challengeId: string
  ) => {
    setSelectedChallengeIds((current) => {
      const updated = new Set(current);

      if (updated.has(challengeId)) {
        updated.delete(challengeId);
      } else {
        updated.add(challengeId);
      }

      return updated;
    });

    setSuccessMessage(null);
  };

  const togglePlayerChallenge = (
    playerName: string,
    challengeId: string
  ) => {
    const key = createPairKey(
      playerName,
      challengeId
    );

    setCompletedPairs((current) => {
      const updated = new Set(current);

      if (updated.has(key)) {
        updated.delete(key);
      } else {
        updated.add(key);
      }

      return updated;
    });

    setSuccessMessage(null);
  };

  const isChallengeCompleted = (
    playerName: string,
    challengeId: string
  ): boolean => {
    return completedPairs.has(
      createPairKey(playerName, challengeId)
    );
  };

  const saveChanges = async () => {
    if (selectedChallengeIdList.length === 0) {
      setError(
        "Válassz ki legalább egy challenge-et a mentéshez."
      );

      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    const items: PlayerChallenge[] = [];

    for (const challengeId of selectedChallengeIdList) {
      for (const standing of standings) {
        if (
          isChallengeCompleted(
            standing.name,
            challengeId
          )
        ) {
          items.push({
            playerName: standing.name,
            challengeId,
          });
        }
      }
    }

    console.log(JSON.stringify(items));
    try {
      const response = await fetch(REPLACE_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(items),
      });

      if (!response.ok) {
        const message = await response.text();

        throw new Error(
          message ||
            "Nem sikerült menteni a teljesítéseket."
        );
      }

      const savedItems =
        (await response.json()) as PlayerChallenge[];

      setCompletedPairs(
        new Set(
          savedItems.map((item) =>
            createPairKey(
              item.playerName,
              item.challengeId
            )
          )
        )
      );

      setSuccessMessage(
        `${items.length} teljesítés sikeresen elmentve.`
      );
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Nem sikerült menteni."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const generateStandings = async () => {
    try {
      const response = await fetch(STANDINGS_GENERATE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const message = await response.text();

        throw new Error(
          message ||
            "Nem sikerült újragenerálni a táblázatot."
        );
      }
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Nem sikerült generálni."
      );
    }
  };

  if (isLoading) {
    return (
      <div className="surface-body">
        <h1>Challenge teljesítések</h1>
        <p>Betöltés...</p>
      </div>
    );
  }

  return (
    <div className="surface-body">
      <h1>Challenge teljesítések</h1>

      <section className="challenge-selector-section">
        <h2 className="mc-title">
          Megjelenített challenge-ek
        </h2>

        <p>
          A kijelölt challenge-ek oszlopként jelennek meg.
          Mentéskor csak ezeknek a challenge-eknek a
          teljesítései frissülnek.
        </p>

        <div className="challenge-selector-grid">
          {weeklyChallenges.map((challenge) => {
            const isSelected =
              selectedChallengeIds.has(challenge.id);

            return (
              <label
                key={challenge.id}
                className={[
                  "challenge-selector-item",
                  isSelected
                    ? "challenge-selector-item-selected"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() =>
                    toggleVisibleChallenge(challenge.id)
                  }
                />

                <span className="challenge-selector-content">
                  <span className="challenge-selector-title">
                    {challenge.title}
                  </span>

                  <span className="challenge-selector-meta">
                    {challenge.weekStart} –{" "}
                    {challenge.weekEnd}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </section>

      {error && (
        <div
          className="challenge-matrix-message challenge-matrix-error"
          role="alert"
        >
          {error}
        </div>
      )}

      {successMessage && (
        <div
          className="challenge-matrix-message challenge-matrix-success"
          role="status"
        >
          {successMessage}
        </div>
      )}

      <section style={{ marginTop: "1.5rem" }}>
        <div className="challenge-matrix-heading">
          <div>
            <h2 className="mc-title">
              Játékosok és teljesítések
            </h2>

            <p>
              {standings.length} játékos,{" "}
              {selectedChallenges.length} kiválasztott
              challenge
            </p>
          </div>

          <button
            type="button"
            className="challenge-matrix-save-button"
            onClick={saveChanges}
            disabled={
              isSaving ||
              selectedChallenges.length === 0
            }
          >
            {isSaving
              ? "Mentés..."
              : "Változások mentése"}
          </button>

          <button
            type="button"
            className="challenge-matrix-save-button"
            onClick={generateStandings}
          >
            "Standings Generate"
          </button>
        </div>

        {selectedChallenges.length === 0 ? (
          <p>
            Jelölj ki legalább egy challenge-et a táblázat
            megjelenítéséhez.
          </p>
        ) : (
          <div className="table-wrap challenge-matrix-wrap">
            <table className="nice-table challenge-matrix-table">
              <thead>
                <tr>
                  <th className="challenge-matrix-player-column">
                    Játékos
                  </th>

                  {selectedChallenges.map((challenge) => {

                    return (
                      <th
                        key={challenge.id}
                        className="challenge-matrix-challenge-column"
                      >
                        <div className="challenge-column-header">
                          <span className="challenge-column-title">
                            {challenge.id}
                          </span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {standings.map((standing) => (
                  <tr key={standing.name}>
                    <th
                      scope="row"
                      className="challenge-matrix-player"
                    >
                      {standing.name}
                    </th>

                    {selectedChallenges.map(
                      (challenge) => {
                        const inputId = [
                          "player-challenge",
                          normalize(standing.name),
                          normalize(challenge.id),
                        ].join("-");

                        return (
                          <td
                            key={challenge.id}
                            className="challenge-matrix-cell"
                          >
                            <input
                              id={inputId}
                              type="checkbox"
                              checked={isChallengeCompleted(
                                standing.name,
                                challenge.id
                              )}
                              onChange={() =>
                                togglePlayerChallenge(
                                  standing.name,
                                  challenge.id
                                )
                              }
                              aria-label={`${standing.name}: ${challenge.title}`}
                            />
                          </td>
                        );
                      }
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}